import { toast } from '@/components/toast';
import { didApiUrl, mixpanelKey, studioDomain, webSocketUrl } from '@/environment';
import { useGetPresenterByID } from '@/hooks';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import anonymous from '@/images/Anonymous2.svg';
import XIcon from '@/images/Close.svg';
import logoutIcon from '@/images/Logout.svg';
import ShareBlack from '@/images/ShareBlack.svg';
import { useGetAgentByClientKey } from '@/pages/agents/api';
import { RemoveEmbedAgentModal } from '@/pages/agents/components/modals';
import { loadAgentsClientScript } from '@/pages/agents/loadAgentsClientScript';
import { isAgentV2 } from '@/pages/avatar/hooks/useAgentIsV2';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useChatSessionsQuery } from '@/state/chat-sessions/useChatSessions';
import { useCreditQuery } from '@/state/credits/credits';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { IAgent, IPresenter } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Menu, MenuItem, Sidebar } from 'react-pro-sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgentShareHeader } from './components/AgentShareHeader';

const maxWidth = '768px';

const AgentShareContainer = styled.div`
    height: 100%;
    background-color: transparent;
    font-size: 16px;
    display: flex;

    * {
        font-family: 'Manrope';
    }
`;

const BodyContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
`;

// next release: to make it wide layout
// aspect-ratio: 4 / 3;
// max-width: 1024px;
const AgentContainer = styled.div<{ isOwner: boolean; isV2: boolean }>`
    height: 100%;
    flex: 1;
    overflow: hidden;
    user-select: text;
    outline: none;
    margin: 20px auto 0;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 16px;
    isolation: isolate;

    ${({ isV2 }) =>
        isV2
            ? `width: auto;
    aspect-ratio: 16 / 9;
    max-width: 100%;
    max-height: 80vh;
`
            : `min-width: 400px;
    max-width: 400px;
    max-height: 700px;`}

    ${({ isOwner }) =>
        !isOwner &&
        `@media (max-width: ${maxWidth}) {
            min-width: 350px;
            width: 100%;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            border-radius: 0;
            border: 0;
            margin: 0;
            position: absolute;
            inset: 0;
    }`}
`;

const StyledSidebar = styled(Sidebar)`
    @media (min-width: 768px) {
        display: none;
    }

    .ps-sidebar-container {
        background-color: white;
    }
`;

const Burger = styled.button`
    display: none;

    background: white;
    border: none;
    cursor: pointer;
    width: 3rem;
    height: 2rem;
    font-size: 2.5rem;
    padding: 0;
    margin: 0;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 2.5rem;
    left: 0.8rem;
    z-index: 1;
`;

const SidebarHeader = styled.div`
    position: relative;
    padding: 0 16px;

    h2 {
        margin: 12px 0 0;
        border-bottom: 1px solid #f1f2f6;
        padding-bottom: 12px;
    }
`;

const Icon = styled.img<{ selected?: boolean; src: string; round?: boolean; flip?: boolean }>`
    display: block;
    border-radius: ${({ round }) => (round ? '50%' : '0')};
    object-fit: contain;
    transform: scale(${({ flip }) => (flip ? -1 : 1)});
`;

const StyledUSerImage = styled(Icon)`
    width: 4rem;
    height: 4rem;
`;

const StyledX = styled(Icon)`
    margin: 16px 8px 0 auto;
    width: 2rem;
    height: 2rem;
    cursor: pointer;
`;

const Footer = styled.footer`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 8px 0 32px 0;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.secondary[900]};

    @media (max-width: ${maxWidth}) {
        display: none;
    }

    a {
        color: ${({ theme }) => theme.colors.secondary[900]};
        text-decoration: none;
        font-weight: 700;
        margin: 0 4px;
        cursor: pointer;
    }
`;

const PageContainer = styled.main`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

interface LocationState {
    isOwner?: boolean;
    agent?: IAgent;
    clientKey?: string;
}

//@ts-ignore
const isAgentPresenterDvp = (presenter?: IPresenter) => {
    return presenter && presenter?.type === 'clip' && presenter.id.startsWith('v2_') && presenter.owner_id !== 'PUBLIC_D-ID';
};

export const AgentSharePage = () => {
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const [removeEmbedModalAgent, setRemoveEmbedModalAgent] = useState<IAgent>();

    const { user, isAuthenticated, loginWithRedirect, logout, isAnonymous } = useAuth();
    const [toggled, setToggled] = useState(false);
    const targetRef = useRef<HTMLDivElement>(null);
    const source = 'agent-gallery';
    const searchParams = new URLSearchParams(useLocation().search);
    const agentIdParam = searchParams.get('id');
    const keyParam = window.atob(searchParams.get('key') ?? '');
    const locationState = useLocation() as { state?: LocationState };
    const { isOwner: isOwnerFromState, agent: agentFromState, clientKey } = locationState.state || {};
    const isOwner = isOwnerFromState !== undefined ? isOwnerFromState : false;
    const agentId = agentFromState?.id ?? agentIdParam;
    const { refetch: refetchCredits } = useCreditQuery();
    const { refetch: refetchKey } = useChatSessionsQuery();
    const key = clientKey ?? keyParam;
    const { data: agent } = useGetAgentByClientKey(agentId || '', key);
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    // @ts-ignore
    const presenter = useGetPresenterByID(agent?.presenter?.presenter_id || '');
    const isV2 = !!isAgentV2(agentId) || searchParams.get('version') === '2';

    useEffect(() => {
        mixpanel?.track('agent-share-page', {
            event: 'view',
            agentId,
            from: source,
        });

        if (!agentId) {
            navigate(AppRoutes.Home);
        } else {
            if (key) {
                const interval = setInterval(() => {
                    refetchCredits();
                    refetchKey();
                }, 1000 * 60);

                loadAgentsClientScript({ isV2 }).then(({ init }) => {
                    init({
                        agentId,
                        clientKey: key,
                        mode: 'full',
                        targetElement: targetRef.current!,
                        monitor: true,
                        onError(err: Error) {
                            console.log(err);

                            try {
                                const jsonError = JSON.parse(err.message);
                                if (jsonError.kind === 'RequestTimeoutError') {
                                    toast.error({
                                        toastId: 'timeout',
                                        title: 'Request timeout, please try again later.',
                                    });
                                } else if (jsonError.description === 'Agent not found') {
                                    toast.error({
                                        toastId: 'agent-not-found',
                                        title: 'The agent you are trying to access does not exist.',
                                    });

                                    navigate(AppRoutes.Agent);
                                }
                            } catch {}
                        },
                        offline: {
                            onSend: () => {
                                mixpanel?.track('go-to-signup', { event: 'click', from: 'agent-share', isAnonymous });
                                loginWithRedirect({ redirectUri: window.location.href });
                            },
                        },
                        didSocketApiUrl: webSocketUrl,
                        didApiUrl,
                        customMixpanelKey: mixpanelKey,
                        externalId: user?.sub ?? '',
                        mixpanelAdditionalProperties: {
                            plan: user?.planGroup,
                        },
                    });
                });

                return () => {
                    clearInterval(interval);
                };
            } else {
                toast.error({
                    toastId: 'invalid key',
                    title: 'Missing agent key',
                    // message: 'There was an error displaying the agent, we are missing your agent key.',
                });
            }
        }
    }, []);

    function onShareClick() {
        mixpanel?.track('agent-share-page', {
            event: 'share',
            agentId,
            from: source,
        });

        navigator.clipboard.writeText(`${studioDomain}${AppRoutes.AgentShare}?id=${agentId}&key=${btoa(key || '')}`);
        toast.info({
            toastId: 'copied',
            title: 'Copied to clipboard',
        });
    }

    return (
        <AgentShareContainer className={isAgentPresenterDvp(presenter) ? 'premium_plus_agent_two_messages_sent' : ''}>
            <Helmet>
                <meta name="robots" content="noindex" />
            </Helmet>
            <PageContainer>
                <AgentShareHeader
                    onEdit={agent => {
                        if (agent?.embed) {
                            setRemoveEmbedModalAgent(agent);
                        } else {
                            navigateToAgentEditor({ agentId: agent?.id as string, mixPanelFrom: 'agent-share' });
                        }
                    }}
                />

                <Burger onClick={() => setToggled(!toggled)}>☰</Burger>
                <StyledSidebar onBackdropClick={() => setToggled(false)} toggled={toggled} breakPoint="all">
                    <SidebarHeader>
                        <StyledX src={XIcon} onClick={() => setToggled(false)} />
                        <StyledUSerImage round src={user?.picture ?? anonymous} />
                        <h2>{isAuthenticated ? user?.name ?? 'Welcome!' : 'Welcome, guest'}</h2>
                    </SidebarHeader>
                    <Menu>
                        <MenuItem icon={<Icon src={ShareBlack} />} onClick={onShareClick}>
                            Share
                        </MenuItem>
                        <MenuItem
                            icon={<Icon src={logoutIcon} flip={!isAuthenticated} />}
                            onClick={() => (isAuthenticated ? logout() : loginWithRedirect())}>
                            {isAuthenticated ? 'logout' : 'Sign in'}
                        </MenuItem>
                    </Menu>
                </StyledSidebar>
                <BodyContainer>
                    <AgentContainer
                        ref={targetRef}
                        isOwner={isOwner}
                        isV2={isV2}
                        className={isOwner ? 'chameleon-agent-chat' : undefined}
                    />
                    <Footer>
                        {!isOwner && (
                            <>
                                Build your own agent with
                                <a href={window.location.origin + AppRoutes.Agent}>D-ID</a>
                            </>
                        )}
                    </Footer>
                </BodyContainer>
            </PageContainer>
            <RemoveEmbedAgentModal
                agent={removeEmbedModalAgent}
                isOpen={!!removeEmbedModalAgent}
                onClose={() => setRemoveEmbedModalAgent(undefined)}
            />
        </AgentShareContainer>
    );
};
